import { CircularProgress, makeStyles } from '@material-ui/core';
import { modeUIContext, setIsEditingForm } from '@moderepo/mode-ui-state';
import React, { CSSProperties, useCallback, useContext, useEffect, useMemo } from 'react';
import { ritzDataContext, selectProjectById } from '@moderepo/mode-data-state-ritz';
import { capitalizeAll } from '@moderepo/mode-app-components';
import clsx from 'clsx';
import { useSelectDevice, useSelectEntity, useSelectHome } from '../../../utils/dataStateHooks';
import { useRitzTranslation } from '../../../utils/customHooks';
import { DeviceRegistrationWizardComp, DeviceRegistrationWizardCompProps } from '.';
import { DeviceRegistrationWizardSettings } from '../../../utils/ritzUIExtensionUtils';


const useStyle = makeStyles(() => {
    return {
        root: {
            width: '100%',
        },
    };
}, { name: 'DeviceRegistrationWizardContainer', index: 1 });



interface DeviceRegistrationWizardContainerProps {
    readonly className?: string | undefined;
    readonly style?: CSSProperties | undefined;
    readonly projectId: number;

    // Optional deviceId. If this is undefined, it means to use the wizard to CREATE a new device. If this is provided, it means
    // to use the wizard to EDIT an existing device
    readonly deviceId?: number | undefined;

    readonly preselectedHomeId?: number | undefined;
    readonly deviceRegistrationWizardSettings: DeviceRegistrationWizardSettings;

    // OPTIONAL handler to be notified when the Register Device is done or cancelled.
    readonly onCancelRegistration?: (()=> void) | undefined;
    readonly onFinishRegistration?: (()=> void) | undefined;
}


export const DeviceRegistrationWizardContainer = (props: DeviceRegistrationWizardContainerProps) => {

    const { dispatch:uiDispatch } = useContext(modeUIContext);
    const { state:dataState } = useContext(ritzDataContext);
    const project = selectProjectById(dataState, props.projectId);

    const home = useSelectHome(props.preselectedHomeId);
    const { trans } = useRitzTranslation(project);
    const classes = useStyle();


    // If "deviceId" is provided, find the device for that deviceId
    const targetDevice = useSelectDevice(props.projectId, props.deviceId);

    // Find an Entity that has the same ID as the device ID. This is probably the entity instance that was created at the same time the
    // device was created. We can assume this entity instance is associated with the device if it exist. NOTE: it is possible that the
    // entity does not exist. This can happen if the device was create from another flow, app, etc...
    const targetEntity = useSelectEntity(props.projectId, props.deviceId ? props.deviceId.toString() : undefined, false, undefined, true);


    /**
     * Set the edit Register Device state to the specified value. This will update the global "isEditing" state.
     */
    const changeDeviceRegistrationWizardState = useCallback((editState: boolean) => {
        // Set the isEditingForm global state
        uiDispatch(setIsEditingForm(editState));
    }, [uiDispatch]);



    /**
     * This Container is used for registering a device therefore we will automatically enable EDIT mode as soon as the component loads
     */
    useEffect(() => {
        changeDeviceRegistrationWizardState(true);
    }, [changeDeviceRegistrationWizardState, uiDispatch]);



    /**
     * Handler for the Cancel registration event from the DeviceRegistrationWizardComp
     */
    const onCancelRegistration = useCallback(() => {
        changeDeviceRegistrationWizardState(false);
        if (props.onCancelRegistration) {
            props.onCancelRegistration();
        }
    }, [changeDeviceRegistrationWizardState, props]);



    /**
     * Handler for the Finish registration event from the DeviceRegistrationWizardComp
     */
    const onFinishRegistration = useCallback(() => {
        changeDeviceRegistrationWizardState(false);
        if (props.onFinishRegistration) {
            props.onFinishRegistration();
        }
    }, [changeDeviceRegistrationWizardState, props]);


    /**
     * Create and cache the actions required for the DeviceRegistrationWizardComp
     */
    const registerDeviceCompActions = useMemo((): DeviceRegistrationWizardCompProps['actions'] => {
        return {
            cancelEdit: {
                label  : capitalizeAll(trans('label.cancel')),
                onClick: onCancelRegistration,
            },
            saveUpdate: {
                label  : capitalizeAll(trans('label.done')),
                onClick: onFinishRegistration,
            },
        };
    }, [onCancelRegistration, onFinishRegistration, trans]);


    return (
        <>
            {project && (!props.preselectedHomeId || home) ? (
                <DeviceRegistrationWizardComp
                    className={clsx(classes.root, props.className, 'editing')}
                    isEditing
                    style={props.style}
                    title={capitalizeAll(trans('device_registration_wizard.panel.title'))}
                    icon="settings_suggest"
                    actions={registerDeviceCompActions}
                    projectId={props.projectId}
                    targetDevice={targetDevice}
                    targetEntity={targetEntity}
                    deviceRegistrationWizardSettings={props.deviceRegistrationWizardSettings}
                    preselectedHomeId={props.preselectedHomeId}
                />
            ) : (
                <CircularProgress />
            )}
        </>
    );
};
